import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import { useAPI } from '@components/APIProvider';

export default function TwilioWebRTCWidget() {
  const api = useAPI();
  const [voiceConfiguration, setVoiceConfiguration] = useState(null);
  const [status, setStatus] = useState('Initializing...'); // Idle, Connecting, Ready, On Call, Disconnected, Error
  const [error, setError] = useState(null);
  const [device, setDevice] = useState(null);
  const [connection, setConnection] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const deviceRef = useRef(null); // Use ref to ensure cleanup uses the correct device instance
  const isMountedRef = useRef(true); // Ref to track mounted state
  const disconnectTimeoutRef = useRef(null); // Ref for disconnect timeout

  const loadVoiceConfiguration = useCallback(() => {
    api.getVoiceConfiguration().then((data) => {
      setVoiceConfiguration(data);
    });
  }, [api]);

  useEffect(() => {
    loadVoiceConfiguration();
  }, [loadVoiceConfiguration]);

  // Fetch Token and Initialize Device
  useEffect(() => {
    let currentDevice = null;
    isMountedRef.current = true;

    const initializeDevice = async () => {
      setStatus('Fetching token...');
      setError(null);
      try {
        const response = await api.fetchTwilioAccessToken();
        if (!response || !response.token) {
          throw new Error('Invalid token received from server.');
        }
        const token = response.token;

        setStatus('Initializing device...');
        currentDevice = new Device(token, {
          logLevel: 'WARN', // any of 'TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'SILENT'
        });
        deviceRef.current = currentDevice;

        currentDevice.on('ready', () => {
          if (isMountedRef.current) {
            setStatus('Ready');
            setDevice(currentDevice);
          }
        });

        currentDevice.on('connect', (conn) => {
          if (isMountedRef.current) {
            if (conn && typeof conn.disconnect === 'function') {
              setStatus('On Call');
              setConnection(conn);
              setIsMuted(conn.isMuted());
              conn.on('mute', (muted) => {
                if (isMountedRef.current) setIsMuted(muted);
              });
            } else {
              console.error(
                'Invalid conn object received in .on("connect"), not setting state.',
                conn
              );
              setError('Received invalid connection object from Twilio.');
              setStatus('Error');
            }
          }
        });

        currentDevice.on('disconnect', () => {
          if (isMountedRef.current) {
            console.debug('[TwilioWidget] Device disconnect event received.');
            clearTimeout(disconnectTimeoutRef.current);
            setStatus('Ready');
            setConnection(null);
          }
        });

        currentDevice.on('error', (twilioError) => {
          console.error('Twilio Device Error:', twilioError);
          if (isMountedRef.current) {
            setError(
              `Device Error: ${twilioError.message} (Code: ${twilioError.code})`
            );
            setStatus('Error');
            if (twilioError.code === 20104) {
              // Handle token expiry
            }
          }
        });

        currentDevice.on('offline', () => {
          if (isMountedRef.current) {
            setStatus('Offline');
            setDevice(null);
          }
        });

        await currentDevice.register();

        if (
          isMountedRef.current &&
          currentDevice.state === 'registered' &&
          status !== 'Ready' &&
          status !== 'On Call'
        ) {
          setStatus('Ready');
          setDevice(currentDevice);
        }
      } catch (err) {
        console.error('Error caught in initializeDevice:', err);
        if (isMountedRef.current) {
          setError(`Initialization failed: ${err.message}`);
          setStatus('Error');
        }
      }
    };

    initializeDevice();

    // Cleanup function
    return () => {
      console.debug('[TwilioWidget] Unmounting component.');
      isMountedRef.current = false;
      if (disconnectTimeoutRef.current) {
        console.debug('[TwilioWidget] Clearing disconnect timeout on unmount.');
        clearTimeout(disconnectTimeoutRef.current);
      }
      if (deviceRef.current) {
        console.debug('[TwilioWidget] Destroying device on unmount.');
        deviceRef.current.removeAllListeners();
        deviceRef.current.destroy();
        deviceRef.current = null; // Clear the ref
      }
      // Reset state on unmount
      setDevice(null);
      setConnection(null);
      setStatus('Unmounted');
      setError(null);
    };
  }, [api]); // Do NOT set dep on 'status'

  const handleConnect = async () => {
    if (!device) {
      setError('Device not ready.');
      return;
    }
    setStatus('Connecting...');
    setError(null);
    try {
      // --- Make Outgoing Call ---
      // You might pass parameters like a 'To' number or agent ID.
      // These params are often sent to your Twilio TwiML App backend via the voiceUrl.
      const call = await device.connect({
        params: {
          // Example: Send parameters to your TwiML application
          To: voiceConfiguration.bot_number, // Or a phone number if calling PSTN via TwiML <Dial>
        },
      });

      if (call && typeof call.disconnect === 'function') {
        setStatus('On Call');
        setConnection(call);
        setIsMuted(call.isMuted());
        call.on('disconnect', () => {
          if (isMountedRef.current) {
            console.debug('[TwilioWidget] Call disconnect event received.');
            clearTimeout(disconnectTimeoutRef.current);
            setStatus('Ready');
            setConnection(null);
          }
        });
        call.on('error', (callError) => {
          if (isMountedRef.current) {
            console.error('Call Error:', callError);
            setError(
              `Call Error: ${callError.message} (Code: ${callError.code})`
            );
            setStatus('Error');
            setConnection(null);
          }
        });
        call.on('mute', (muted) => {
          if (isMountedRef.current) {
            setIsMuted(muted);
          }
        });
      } else {
        console.error(
          'Invalid call object returned from device.connect(), NOT setting connection state.',
          call
        );
        if (!error && isMountedRef.current) {
          setError('Connection attempt returned an invalid object.');
          setStatus('Error');
        }
      }
    } catch (err) {
      console.error('Error caught in handleConnect:', err);
      if (isMountedRef.current) {
        setError(`Connection Error: ${err.message}`);
        setStatus('Ready');
      }
    }
  };

  const handleDisconnect = () => {
    clearTimeout(disconnectTimeoutRef.current); // Clear any pending timeout first

    if (connection) {
      connection.disconnect();
    } else if (device) {
      // If there's no active connection but the device is still there,
      // ensure all potential connections are cleared.
      device.disconnectAll();
    }
    setStatus('Disconnecting...');

    // Set a timeout to force reset state if disconnect event doesn't fire
    disconnectTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        // Check if component is still mounted
        setStatus((prevStatus) => {
          if (prevStatus === 'Disconnecting...') {
            console.warn(
              '[TwilioWidget] Disconnect event timed out. Forcing state reset.'
            );
            // Ensure connection is also cleared when forcing state
            setConnection(null);
            return 'Ready'; // Return the new state
          } else {
            // If status changed before timeout (e.g., disconnect event *did* fire)
            return prevStatus; // Keep the current status
          }
        });
      } else {
        console.debug('[TwilioWidget] Timeout fired but component unmounted.');
      }
    }, 5000); // 5 seconds timeout
  };

  const handleToggleMute = () => {
    if (connection) {
      const shouldMute = !isMuted;
      connection.mute(shouldMute);
      setIsMuted(shouldMute);
    }
  };

  const canConnect = device && (status === 'Ready' || status === 'Offline'); // Allow connect if ready or recovered from offline
  const canDisconnect = !!connection;
  const canToggleMute = !!connection;

  return (
    <Box
      sx={{
        padding: 2,
        minHeight: '150px', // Keep minHeight as fallback
        height: '100%', // Explicitly set height to fill container
        display: 'flex', // Use flexbox for layout
        flexDirection: 'column', // Stack children vertically
        boxSizing: 'border-box', // Ensure padding is included in height
        bgcolor: 'background.paper', // Add background color
      }}
    >
      <Typography variant="h6" gutterBottom>
        Voice Agent Connection
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body1" sx={{ mb: 2 }}>
        Status: {status}
        {(status === 'Initializing...' ||
          status === 'Fetching token...' ||
          status === 'Initializing device...' ||
          status === 'Connecting...') && (
          <CircularProgress size={16} sx={{ ml: 1 }} />
        )}
      </Typography>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          color="success"
          startIcon={<MicIcon />}
          onClick={handleConnect}
          disabled={!canConnect}
        >
          Connect
        </Button>

        <Button
          variant="contained"
          color="warning"
          startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
          onClick={handleToggleMute}
          disabled={!canToggleMute}
        >
          {isMuted ? 'Unmute' : 'Mute'}
        </Button>

        <Button
          variant="contained"
          color="error"
          startIcon={<CallEndIcon />}
          onClick={handleDisconnect}
          disabled={!canDisconnect}
        >
          Disconnect
        </Button>
      </Stack>
    </Box>
  );
}
